import { cloneDeep } from 'lodash';
import { STEP_TYPES } from './templates';

const insertFollowupItemsInItems = (rootItems, followupItems) => {
  return rootItems?.map((rootItem) => {
    // Get immediate followups for this root item
    const immediateFollowups = followupItems?.filter(
      (item) =>
        item?.ParentId === rootItem?.ChecklistItemId ||
        item?.ParentId === rootItem?.id ||
        item?.ParentId === rootItem?.ChecklistSectionId,
    );

    // Recursively process each followup to get their followups
    const processedFollowups = immediateFollowups?.map((followup) => {
      // Get followups for this followup item
      const nestedFollowups = followupItems?.filter(
        (item) =>
          item?.ParentId === followup?.ChecklistItemId ||
          item?.ParentId === followup?.id ||
          item?.ParentId === followup?.ChecklistSectionId,
      );

      return {
        ...followup,
        followUps: insertFollowupItemsInItems(nestedFollowups, followupItems),
      };
    });

    return {
      ...rootItem,
      followUps: processedFollowups,
    };
  });
};

//Organize Checklist Items and Sections
const organizeChecklistIntoLinear = (data, flatFollowUps = true) => {
  const updated = organizeChecklistIntoHierarchy(data);
  const allItems = hierarchyToLinear(updated, flatFollowUps);
  return allItems;
};

const renameFollowUpsRecursively = (item: any): any => {
  const newItem = { ...item };

  if (newItem.followUps && Array.isArray(newItem.followUps)) {
    newItem.ChildItems = newItem.followUps.map((child) =>
      renameFollowUpsRecursively(child),
    );
  }

  delete newItem.followUps;
  return newItem;
};

const hierarchyToLinear = (data: any[], flatFollowUps = true) => {
  const allItems: any[] = [];

  // Recursive processor with parent tracking
  const processItem = (
    item: any,
    parentSectionId: any = null,
    noInjectParentSectionId = false,
  ) => {
    const flatItem = { ...item };
    delete flatItem.checklistItems;
    delete flatItem.subSections;

    if (!flatFollowUps) {
      const followUps = cloneDeep(flatItem?.followUps ?? []);
      flatItem.ChildItems = followUps?.map((child) => {
        return renameFollowUpsRecursively(child);
      });
    }

    delete flatItem.followUps;

    if (!noInjectParentSectionId) {
      flatItem.ParentSectionId = parentSectionId;
    }

    allItems.push(flatItem);

    if (item.ChecklistItems && Array.isArray(item.ChecklistItems)) {
      item.ChecklistItems.forEach((child) => processItem(child, item.id));
    }

    if (item.TaskChecklistItems && Array.isArray(item.TaskChecklistItems)) {
      item.TaskChecklistItems.forEach((child) => processItem(child, item.id));
    }

    if (flatFollowUps) {
      if (item.followUps && Array.isArray(item.followUps)) {
        item.followUps
          .filter((item) => item?.ParentId)
          ?.forEach((child) => processItem(child, item.id, true));
      }
    }

    if (item.subSections && Array.isArray(item.subSections)) {
      item.subSections.forEach((child) => processItem(child, item.id));
    }
  };

  data.forEach((item) => processItem(item));

  return allItems;
};

const insertSubSections = (
  parentSections,
  allSections,
  checklistProperty,
  sectionsProperty,
) => {
  return parentSections?.map((section) => {
    // Find direct children of this section
    const directSubSections = allSections?.filter(
      (s) => s?.ParentSectionId === section?.id,
    );

    // Recursively build each subsection
    const nestedSubSections = insertSubSections(
      directSubSections,
      allSections,
      checklistProperty,
      sectionsProperty,
    );

    const checklistItems = section?.[checklistProperty];

    const rootItems = checklistItems
      ?.filter((item) => !item?.ParentId)
      .sort((a, b) => a.order - b.order);

    const followUpChecklistItems = checklistItems
      ?.filter((item) => item?.ParentId)
      .sort((a, b) => a.order - b.order);

    const followUpChecklistSections = allSections
      ?.filter((item) => item?.ParentId)
      .sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        ...item,
        type: STEP_TYPES.HEADER,
      }));

    const followUpItems = [
      ...(followUpChecklistItems ?? []),
      ...(followUpChecklistSections ?? []),
    ];

    let updatedRootItems = insertFollowupItemsInItems(rootItems, followUpItems);

    updatedRootItems = updatedRootItems?.map((item) => {
      const processFollowUps = (followUpItem) => {
        const allFollowups: any[] = [];

        followUpItem?.followUps?.forEach((child) => {
          if (child?.type === STEP_TYPES.HEADER) {
            const transformedSection = {
              ...child,
              ParentId: null,
              RelatedAutomationId: null,
              ParentSectionId: null,
            };
            let sectionLinear = organizeChecklistIntoLinear({
              ChecklistSections: [transformedSection],
              ChecklistItems: [],
            });

            sectionLinear = sectionLinear.map((item) => {
              if (item?.id === child?.id) {
                return child;
              }
              return item;
            });
            sectionLinear?.forEach((item) => {
              allFollowups.push(item);
            });
          } else {
            // Process nested followUps recursively
            const processedChild = {
              ...child,
              followUps: processFollowUps(child),
            };
            allFollowups.push(processedChild);
          }
        });

        // Add items whose ParentSectionId matches child item's ID
        followUpItem?.followUps?.forEach((child) => {
          const itemsWithMatchingParentSection = followUpItems?.filter(
            (item) => item?.ParentSectionId === child?.id,
          );
          if (itemsWithMatchingParentSection?.length > 0) {
            allFollowups.push(...itemsWithMatchingParentSection);
          }
        });

        return allFollowups;
      };

      return {
        ...item,
        followUps: processFollowUps(item),
      };
    });

    return {
      ...section,
      isActive: section?.isActive ?? true,
      type: STEP_TYPES.HEADER,
      [checklistProperty]: updatedRootItems,
      subSections: nestedSubSections,
    };
  });
};

export const organizeChecklistIntoHierarchy = (data) => {
  let sectionsProperty = 'TaskChecklistSections';
  if (data?.ChecklistSections) {
    sectionsProperty = 'ChecklistSections';
  }

  let checklistProperty = 'TaskChecklistItems';
  if (data?.ChecklistItems) {
    checklistProperty = 'ChecklistItems';
  }
  const checklist = {
    [checklistProperty]: data?.[checklistProperty],
    [sectionsProperty]: data?.[sectionsProperty],
  };

  const rootItems = checklist?.[checklistProperty]
    ?.filter((item) => !item?.ParentId)
    .sort((a, b) => a.order - b.order);

  const followUpChecklistItems = checklist?.[checklistProperty]?.filter(
    (item) => item?.ParentId,
  );

  const followUpChecklistSections = checklist?.[sectionsProperty]
    ?.filter((item) => item?.ParentId)
    ?.map((item) => ({
      ...item,
      type: STEP_TYPES.HEADER,
    }));

  const followUpItems = [
    ...(followUpChecklistItems ?? []),
    ...(followUpChecklistSections ?? []),
  ].sort((a, b) => a.order - b.order);

  let updatedRootItems = insertFollowupItemsInItems(rootItems, followUpItems);

  updatedRootItems = updatedRootItems?.map((item) => {
    const processFollowUps = (followUpItem) => {
      const allFollowups: any[] = [];

      followUpItem?.followUps?.forEach((child) => {
        if (child?.type === STEP_TYPES.HEADER) {
          const transformedSection = {
            ...child,
            ParentId: null,
            RelatedAutomationId: null,
            ParentSectionId: null,
          };
          let sectionLinear = organizeChecklistIntoLinear({
            ChecklistSections: [transformedSection],
            ChecklistItems: [],
          });

          sectionLinear = sectionLinear.map((item) => {
            if (item?.id === child?.id) {
              return child;
            }
            return item;
          });
          sectionLinear?.forEach((item) => {
            allFollowups.push(item);
          });
        } else {
          // Process nested followUps recursively
          const processedChild = {
            ...child,
            followUps: processFollowUps(child),
          };
          allFollowups.push(processedChild);
        }
      });

      // Add items whose ParentSectionId matches child item's ID
      followUpItem?.followUps?.forEach((child) => {
        const itemsWithMatchingParentSection = followUpItems?.filter(
          (item) => item?.ParentSectionId === child?.id,
        );
        if (itemsWithMatchingParentSection?.length > 0) {
          allFollowups.push(...itemsWithMatchingParentSection);
        }
      });

      return allFollowups;
    };

    return {
      ...item,
      followUps: processFollowUps(item),
    };
  });

  const parentSections = checklist?.[sectionsProperty]?.filter(
    (section) => !section?.ParentSectionId && !section?.ParentId,
  );

  let updatedSections = insertSubSections(
    parentSections,
    checklist?.[sectionsProperty],
    checklistProperty,
    sectionsProperty,
  );
  updatedSections = updatedSections?.map((section) => {
    return {
      ...section,
      isActive: section?.isActive ?? true,
      type: STEP_TYPES.HEADER,
    };
  });

  return [...(updatedRootItems ?? []), ...(updatedSections ?? [])];
};
